import * as React from 'react';

import {
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from '@floating-ui/react';

interface ContentProps {
  children: React.ReactNode;
}

interface Props {
  children: React.ReactNode;
  dialog: DialogProps;
  contentComponent?: React.ComponentType<ContentProps>;
}

export interface DialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  refs: ReturnType<typeof useFloating>['refs'];
  context: ReturnType<typeof useFloating>['context'];
}

export const useDialog = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const openDialog = () => setIsOpen(true);
  const closeDialog = () => setIsOpen(false);

  return { isOpen, openDialog, closeDialog, setIsOpen, refs, context };
};

export const Dialog = ({ dialog, children, contentComponent: Content = ContentDiv }: Props) => {
  const { context } = useFloating({
    open: dialog.isOpen,
    onOpenChange: dialog.setIsOpen,
  });
  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });

  const { getFloatingProps } = useInteractions([click, role, dismiss]);

  return (
    <FloatingPortal>
      {dialog.isOpen && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black bg-opacity-80">
          <FloatingFocusManager context={context}>
            <div ref={dialog.refs.setFloating} {...getFloatingProps()}>
              <Content>{children}</Content>
            </div>
          </FloatingFocusManager>
        </div>
      )}
    </FloatingPortal>
  );
};

const ContentDiv: React.FC<ContentProps> = ({ children }) => (
  <div className="relative rounded-lg border border-pink-500 bg-background-canvas p-8 text-center text-white shadow-lg">
    {children}
  </div>
);

// export const DialogActions = ({ children }: { children: React.ReactNode }) => (
//   <div className="mt-5 flex justify-between gap-8">{children}</div>
// );

export const StyledHeading = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) => (
  <div className={`text-center text-2xl font-medium leading-9 text-white ${className}`}>
    {children}
  </div>
);

export const DialogBodyText = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) => <div className={`mb-8 mt-5 text-base text-white ${className}`}>{children}</div>;
